import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CLIENT_ROLES_LIST_API,
  CLIENT_USERS_UPDATE_API,
  DEPARTMENT_LIST_API,
} from '../../utils/api';

function UserCard({
  selectedDept,
  users = [],
  newUser,
  setNewUser,
  onAddUser,
  reloadUsers,
  searchTerm,
  setSearchTerm,
  loadingUsers,
  savingUser,
}) {
  const token = localStorage.getItem('token');
  const parsed = token ? JSON.parse(atob(token)) : null;
  const customer_id = parsed?.customer_id;

  const [roleOptions, setRoleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editForm, setEditForm] = useState({});

  const authHeaders = useMemo(() => ({ Authorization: token || '' }), [token]);

  const normalizeDepartments = (arr) =>
    (arr || []).map((d) => ({
      id: Number(d.id ?? d.department_id),
      name: d.name ?? d.department_name,
    }));

  // Load roles + departments once
  useEffect(() => {
    const loadInit = async () => {
      try {
        const [rolesRes, deptRes] = await Promise.all([
          axios.get(CLIENT_ROLES_LIST_API, { headers: authHeaders }),
          axios.post(
            DEPARTMENT_LIST_API,
            { customer_id, status: 1 },
            { headers: authHeaders }
          ),
        ]);

        if (rolesRes.data?.success) {
          const rolesRaw = rolesRes.data.roles || [];
          const roles = rolesRaw.map((r) => ({
            id: r.id ?? r.client_roll_id,
            name: r.name ?? r.roll_name,
          }));
          setRoleOptions(roles);
        }

        if (deptRes.data?.success) {
          const list = normalizeDepartments(deptRes.data.rows || deptRes.data.departments);
          setDepartmentOptions(list);
        }
      } catch (e) {
        console.error('❌ Init load failed:', e);
      }
    };
    if (token && customer_id) loadInit();
  }, [token, customer_id, authHeaders]);

  // Fetch departments fresh (e.g. before editing)
  const refreshDepartments = async () => {
    try {
      const res = await axios.post(
        DEPARTMENT_LIST_API,
        { customer_id, status: 1 },
        { headers: authHeaders }
      );
      if (res.data?.success) {
        const list = normalizeDepartments(res.data.rows || res.data.departments);
        setDepartmentOptions(list);
      }
    } catch (e) {
      console.error('❌ Fetch departments error:', e);
    }
  };

  const handleEdit = async (index, user) => {
    await refreshDepartments();
    setEditIndex(index);
    setEditForm({
      id: user.id,
      name: user.name,
      mobile: user.mobile,
      role: user.role,
      department_id: user.department_id,
      client_user_token: user.client_user_token,
      status: user.status,
    });
  };

  const handleCancel = () => {
    setEditIndex(null);
    setEditForm({});
  };

  const handleSave = async () => {
    try {
      const updated_by = parsed?.id;
      const res = await axios.post(
        CLIENT_USERS_UPDATE_API,
        {
          ...editForm,
          customer_id,
          updated_by,
        },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        alert('✅ User updated');
        handleCancel();
        reloadUsers && reloadUsers();
      } else {
        alert(res.data?.message || 'Update failed');
      }
    } catch (e) {
      console.error('❌ Save error:', e);
      alert('Error saving user');
    }
  };

  const handleStatusToggle = async (user) => {
    if (!window.confirm(`Are you sure to ${user.status === 1 ? 'Deactivate' : 'Activate'} this user?`))
      return;

    try {
      const updated_by = parsed?.id;
      const res = await axios.post(
        CLIENT_USERS_UPDATE_API,
        {
          id: user.id,
          client_user_token: user.client_user_token,
          customer_id,
          updated_by,
          status: user.status === 1 ? 0 : 1,
          name: user.name,
          mobile: user.mobile,
          role: user.role,
          department_id: user.department_id,
        },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        alert('✅ Status updated');
        reloadUsers && reloadUsers();
      } else {
        alert(res.data?.message || 'Status update failed');
      }
    } catch (e) {
      console.error('❌ Status toggle error:', e);
      alert('Error toggling user status');
    }
  };

  return (
    <div className="card shadow p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">
          Users {selectedDept ? <small className="text-muted">in {selectedDept.name}</small> : null}
        </h5>
        {savingUser && <span className="badge bg-warning text-dark">Saving…</span>}
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder={`🔍 Search by Name or Mobile${
            selectedDept?.name ? ` in ${selectedDept.name} Department` : ''
          }`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {!selectedDept ? (
        <div className="text-muted py-4 text-center">Select a department to manage users.</div>
      ) : loadingUsers ? (
        <div className="text-muted py-4 text-center">⏳ Loading users…</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-sm mt-2 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 160 }}>Name</th>
                  <th style={{ minWidth: 120 }}>Mobile</th>
                  <th style={{ minWidth: 140 }}>Role</th>
                  <th style={{ minWidth: 160 }}>Department</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.id}>
                    {editIndex === index ? (
                      <>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={editForm.mobile || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d{0,10}$/.test(val)) {
                                setEditForm({ ...editForm, mobile: val });
                              }
                            }}
                          />
                        </td>
                        <td>
                          <select
                            className="form-control"
                            value={editForm.role || ''}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          >
                            <option value="">Select Role</option>
                            {roleOptions.map((r) => (
                              <option key={r.id} value={r.name}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            className="form-control"
                            value={editForm.department_id || ''}
                            onChange={(e) =>
                              setEditForm({ ...editForm, department_id: Number(e.target.value) })
                            }
                          >
                            <option value="">Select Department</option>
                            {departmentOptions.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>{editForm.status === 1 ? '🟢 Active' : '⚫ Inactive'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-success" onClick={handleSave}>
                              💾 Save
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={handleCancel}>
                              ✖ Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{user.name}</td>
                        <td>{user.mobile}</td>
                        <td>{user.role}</td>
                        <td>{user.department_name || '-'}</td>
                        <td>
                          <button
                            className={`btn btn-sm ${
                              user.status === 1 ? 'btn-success' : 'btn-secondary'
                            }`}
                            onClick={() => handleStatusToggle(user)}
                          >
                            {user.status === 1 ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEdit(index, user)}
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ➕ Add New User */}
          <h6 className="mt-4">➕ Add New User</h6>
          <div className="row g-2">
            <div className="col-md-3">
              <input
                type="text"
                className={`form-control ${
                  newUser.name.length >= 0 && newUser.name.length < 3 ? 'is-invalid' : ''
                }`}
                placeholder="Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className={`form-control ${
                  newUser.mobile.length >= 0 && newUser.mobile.length < 10 ? 'is-invalid' : ''
                }`}
                placeholder="Mobile"
                value={newUser.mobile}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d{0,10}$/.test(val)) {
                    setNewUser({ ...newUser, mobile: val });
                  }
                }}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="">Select Role</option>
                {roleOptions.map((r) => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="text"
                className="form-control"
                placeholder="Password (optional)"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
            <div className="col-md-2 d-grid">
              <button className="btn btn-success" onClick={onAddUser} disabled={savingUser}>
                ➕ Add User
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UserCard;
